import { Box, Divider, IconButton, Typography } from '@mui/material';
import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import { DateFormats } from '@uplift/constants';
import { DateObject } from '@uplift/services';
import React, { useCallback, useMemo } from 'react';
import { useStyles } from '../styles';
import { OnSetCurrentDateType } from '../types';

export type SelectDateProps = {
  currentDate: DateObject;
  onSetCurrentDate: OnSetCurrentDateType;
  backDisabled?: boolean;
  forwardDisabled?: boolean;
  dateFormat?: string;
  isTriad?: boolean;
  onDisabledForwardClick?: () => void;
};

export const SelectDate: React.FC<SelectDateProps> = props => {
  const { classes } = useStyles();

  const onForward = useCallback(() => {
    props.onSetCurrentDate(props.currentDate.add(1, 'day').startOf('day'));
  }, [ props ]);

  const onBack = useCallback(() => {
    props.onSetCurrentDate(props.currentDate.subtract(1, 'day').startOf('day'));
  }, [ props ]);

  const dateFormat = props.dateFormat ?? DateFormats.fullWeekdayFullMonthDate;
  const formattedDate = props.currentDate.format(dateFormat);

  const onForwardClickHandler = useCallback(() => {
    if (props.forwardDisabled) {
      props.onDisabledForwardClick?.();
    } else {
      onForward();
    }
  }, [ onForward, props ]);

  const withTriadWrapper = (content: React.ReactNode) => {
    return (
      <Box className={classes.backgroundWhite}>
        <Box py={1} textAlign="center">
          <Typography variant="body2">Select an available time</Typography>
        </Box>
        <Divider />
        {content}
      </Box>
    );
  };

  const content = useMemo(() => (
    <Box
      display="flex"
      alignItems="center"
      px={!props.isTriad && 3}
      pt={props.isTriad && 2}
    >
      <IconButton
        color="secondary"
        onClick={onBack}
        disabled={props.backDisabled}
        data-testid="prevAvailableDay"
      >
        <KeyboardArrowLeft />
      </IconButton>

      {!props.isTriad && (
        <Box flex={1} textAlign="center">
          <Typography className={classes.userSelectNone}>{formattedDate}</Typography>
        </Box>
      )}

      {props.isTriad && (
        <Box display="flex" flex={1} textAlign="center">
          {Array.from({ length: 3 }, (_, i) => {
            const date = props.currentDate.add(i, 'day');
            const weekday = date.format('dddd');
            const formattedDate = date.format('MMM D');

            return (
              <Box key={i} flex={1}>
                <Typography className={classes.userSelectNone} variant="subtitle2">{weekday}</Typography>
                <Typography className={classes.userSelectNone} variant="subtitle2">{formattedDate}</Typography>
              </Box>
            );
          })}
        </Box>
      )}

      <IconButton
        color="secondary"
        onClick={onForwardClickHandler}
        className={props.forwardDisabled ? 'Mui-disabled' : undefined}
        style={{ pointerEvents: 'auto' }}
        disableRipple={props.forwardDisabled}
        data-testid="nextAvailableDay"
      >
        <KeyboardArrowRight />
      </IconButton>
    </Box>
  ), [ classes.userSelectNone, formattedDate, onBack, onForwardClickHandler, props.backDisabled, props.currentDate, props.forwardDisabled, props.isTriad ]);

  return props.isTriad ? withTriadWrapper(content) : content;
};
